import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/forms/fields";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, BadgeCheck, Check, Pencil } from "lucide-react";

const TIPOS = [
  ["representante_legal", "Representante legal"],
  ["apoderado", "Apoderado"],
  ["firmante", "Firmante"],
  ["contacto_administrativo", "Contacto administrativo"],
  ["contacto_facturacion", "Contacto de facturación"],
  ["responsable_operativo", "Responsable operativo"],
  ["contacto_tecnico", "Contacto técnico"],
];
const empty = {
  nombre_completo: "", tipos: [], es_firmante_principal: false, cargo: "", email: "", telefono: "",
  instrumento_tipo: "", instrumento_numero: "", instrumento_fecha: "",
  notario_nombre_o_numero: "", notaria_ciudad_estado: "",
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// UI-004: tipos que SÍ ejercen representación/firma y por tanto requieren acreditar facultades.
const TIPOS_CON_FACULTADES = ["representante_legal", "apoderado", "firmante"];
const requiereFacultades = (p) =>
  !!p.es_firmante_principal || (p.tipos || []).some((t) => TIPOS_CON_FACULTADES.includes(t));

function EstadoChip({ ok, okTexto, pendienteTexto, testId }) {
  return (
    <span data-testid={testId}
      className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 font-medium ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
      {ok && <Check className="h-3 w-3" strokeWidth={2.5} />}
      {ok ? okTexto : pendienteTexto}
    </span>
  );
}

export default function RepresentacionTab({ oid, onReload }) {
  const [personas, setPersonas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [tocado, setTocado] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = () => api.get(`/organizations/${oid}/personas`).then((r) => setPersonas(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [oid]);

  const toggleTipo = (t) =>
    setF((p) => ({ ...p, tipos: p.tipos.includes(t) ? p.tipos.filter((x) => x !== t) : [...p.tipos, t] }));

  const abrirNueva = () => { setEditId(null); setF(empty); setTocado(false); setOpen(true); };
  const abrirEditar = (p) => {
    setEditId(p.id);
    setF({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, p[k] ?? empty[k]])) });
    setTocado(false);
    setOpen(true);
  };

  const emailErr = (tocado || f.email) && f.email && !EMAIL_RE.test(f.email.trim())
    ? "Correo inválido. Revisa el formato (nombre@dominio.com)." : "";
  const nombreErr = tocado && !f.nombre_completo.trim() ? "El nombre completo es obligatorio." : "";
  const puedeGuardar = !!f.nombre_completo.trim() && !emailErr;

  const guardar = async () => {
    setTocado(true);
    if (!puedeGuardar) return;
    setSaving(true);
    try {
      if (editId) await api.put(`/organizations/${oid}/personas/${editId}`, f);
      else await api.post(`/organizations/${oid}/personas`, f);
      notifyOk(editId ? "Persona actualizada." : "Persona registrada.");
      setOpen(false);
      setEditId(null);
      setF(empty);
      load();
      onReload && onReload();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const verificar = async (p, campo) => {
    try {
      await api.post(`/organizations/${oid}/personas/${p.id}/verificaciones`, { [campo]: true });
      notifyOk(campo === "identidad" ? "Identidad verificada." : "Facultades verificadas.");
      load();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const marcarFirmante = async (p) => {
    try {
      await api.put(`/organizations/${oid}/personas/${p.id}`, { ...p, es_firmante_principal: true });
      notifyOk("Firmante principal actualizado.");
      load();
      onReload && onReload();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const desactivar = async (p) => {
    try {
      await api.post(`/organizations/${oid}/personas/${p.id}/desactivar`);
      load();
      onReload && onReload();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const activas = personas.filter((p) => p.estado !== "Inactivo");
  const sinFirmante = activas.length > 0 && !activas.some((p) => p.es_firmante_principal);

  return (
    <div className="space-y-5" data-testid="tab-representacion">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-xl">
          Estas personas NO son usuarios del sistema y no otorgan permisos. La identificación oficial
          no se sube: sólo se registra el acto humano de verificación.
        </p>
        <Button data-testid="add-org-persona-button" onClick={abrirNueva}
          className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white shrink-0 w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-1.5" /> Agregar persona
        </Button>
      </div>

      {sinFirmante && (
        <div data-testid="aviso-sin-firmante" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-xs text-amber-800">
            Ninguna persona está marcada como firmante principal del contrato. Usa «Marcar firmante»
            en la persona que corresponda. Esto no bloquea nada operativo.
          </p>
        </div>
      )}

      {personas.length === 0 ? (
        <p data-testid="org-personas-empty" className="text-slate-400 text-sm">Sin personas registradas.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {personas.map((p) => (
            <div key={p.id} data-testid={`org-persona-${p.id}`} className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-[#012472] break-words">{p.nombre_completo}</p>
                  {p.es_firmante_principal && (
                    <span data-testid={`badge-firmante-${p.id}`}
                      className="text-[10px] uppercase tracking-wide rounded-full bg-[#01CBE3]/20 text-[#012472] px-2 py-0.5 font-bold">
                      Firmante principal
                    </span>
                  )}
                  {p.estado === "Inactivo" && <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">Inactiva</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 break-words">
                  {(p.tipos || []).map((t) => (TIPOS.find(([k]) => k === t) || [t, t])[1]).join(" · ") || "Sin tipo"}
                  {p.cargo ? ` · ${p.cargo}` : ""}{p.email ? ` · ${p.email}` : ""}{p.telefono ? ` · ${p.telefono}` : ""}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <EstadoChip ok={!!p.identidad_verificada_en} okTexto="Identidad verificada"
                    pendienteTexto="Identidad pendiente" testId={`chip-identidad-${p.id}`} />
                  {/* UI-004: la verificación de facultades sólo aplica a quien ejerce
                      representación/firma (señal existente: `tipos` + `es_firmante_principal`).
                      Un contacto operativo/técnico no arrastra un pendiente legal que no le toca. */}
                  {requiereFacultades(p)
                    ? <EstadoChip ok={!!p.facultades_verificadas_en} okTexto="Facultades verificadas"
                        pendienteTexto="Facultades pendientes" testId={`chip-facultades-${p.id}`} />
                    : <span data-testid={`chip-facultades-na-${p.id}`} className="inline-flex items-center text-[11px] rounded-full px-2 py-0.5 font-medium bg-slate-50 text-slate-400">
                        Sin función de representación
                      </span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {!p.identidad_verificada_en && (
                  <Button variant="outline" size="sm" className="rounded-lg" data-testid={`verificar-identidad-${p.id}`} onClick={() => verificar(p, "identidad")}>
                    <BadgeCheck className="h-4 w-4 mr-1" /> Verificar identidad
                  </Button>
                )}
                {requiereFacultades(p) && !p.facultades_verificadas_en && (
                  <Button variant="outline" size="sm" className="rounded-lg" data-testid={`verificar-facultades-${p.id}`} onClick={() => verificar(p, "facultades")}>
                    <BadgeCheck className="h-4 w-4 mr-1" /> Verificar facultades
                  </Button>
                )}
                {p.estado !== "Inactivo" && !p.es_firmante_principal && (
                  <Button variant="outline" size="sm" className="rounded-lg" data-testid={`marcar-firmante-${p.id}`} onClick={() => marcarFirmante(p)}>
                    Marcar firmante
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="rounded-lg text-slate-500" data-testid={`editar-org-persona-${p.id}`} onClick={() => abrirEditar(p)}>
                  <Pencil className="h-4 w-4 mr-1" /> Editar
                </Button>
                {p.estado !== "Inactivo" && (
                  <Button variant="ghost" size="sm" className="rounded-lg text-slate-500" data-testid={`desactivar-org-persona-${p.id}`} onClick={() => desactivar(p)}>
                    Desactivar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#012472]">
              {editId ? "Editar persona de la organización" : "Persona de la organización"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Registro administrativo. No crea un usuario del sistema ni otorga permisos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Nombre completo" error={nombreErr} errorTestId="org-persona-nombre-error">
              <Input data-testid="org-persona-nombre" className="rounded-xl" value={f.nombre_completo}
                onChange={(e) => set("nombre_completo", e.target.value)} />
            </Field>
            <div>
              <p className="text-sm font-medium text-[#012472] mb-2">Tipos (puede tener varios)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TIPOS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-xs text-slate-600">
                    <Checkbox checked={f.tipos.includes(k)} onCheckedChange={() => toggleTipo(k)} data-testid={`org-persona-tipo-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 rounded-xl border border-[#01CBE3]/40 bg-[#01CBE3]/5 px-3 py-3 text-sm text-[#012472]">
              <Checkbox className="mt-0.5" checked={f.es_firmante_principal}
                onCheckedChange={(v) => set("es_firmante_principal", !!v)} data-testid="org-persona-firmante" />
              <span>
                Firmante principal del contrato
                <span className="block text-[11px] font-normal text-slate-500">
                  Sólo una persona por organización. Al marcarla, se desmarca la anterior.
                </span>
              </span>
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Cargo (opcional)"><Input className="rounded-xl" value={f.cargo} onChange={(e) => set("cargo", e.target.value)} /></Field>
              <Field label="Correo electrónico" error={emailErr} errorTestId="org-persona-email-error">
                <Input data-testid="org-persona-email" type="email" inputMode="email" autoCapitalize="none"
                  autoCorrect="off" className="rounded-xl" value={f.email} onChange={(e) => set("email", e.target.value)} />
              </Field>
              <Field label="Teléfono"><Input data-testid="org-persona-telefono" type="tel" inputMode="tel" className="rounded-xl" value={f.telefono} onChange={(e) => set("telefono", e.target.value)} /></Field>
              <Field label="Instrumento (tipo)"><Input className="rounded-xl" placeholder="Acta / poder" value={f.instrumento_tipo} onChange={(e) => set("instrumento_tipo", e.target.value)} /></Field>
              <Field label="Número de instrumento"><Input className="rounded-xl" value={f.instrumento_numero} onChange={(e) => set("instrumento_numero", e.target.value)} /></Field>
              <Field label="Fecha de instrumento"><Input type="date" className="rounded-xl" value={f.instrumento_fecha} onChange={(e) => set("instrumento_fecha", e.target.value)} /></Field>
              <Field label="Notario (nombre o número)"><Input className="rounded-xl" value={f.notario_nombre_o_numero} onChange={(e) => set("notario_nombre_o_numero", e.target.value)} /></Field>
              <Field label="Notaría (ciudad/estado)"><Input className="rounded-xl" value={f.notaria_ciudad_estado} onChange={(e) => set("notaria_ciudad_estado", e.target.value)} /></Field>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="org-persona-guardar" disabled={saving || !puedeGuardar}
              className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={guardar}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
